import {
  AuctionStatusEnum,
  SlimAuctionsEntity,
} from 'libs/gb-dashboard/auctions/data-access/src/lib/+state/auctions.models'
import {
  PaymentGatewayEnum,
  PaymentMethodFeeTypeEnum,
  PaymentMethodsEntity,
} from 'libs/gb-dashboard/setup/data-access-payment-methods/src/lib/+state/payment-methods.models'
import { StripeConnectEntity } from 'libs/gb-dashboard/shared/data-access-stripe-connect/src/lib/+state/stripe-connect.models'
import { CaptureAuctionFieldEnum } from 'libs/shared/models/auction/auction.models'
import { ImagesEntity } from 'libs/shared/models/images/image.models'

export enum EventLocationTypeEnum {
  InPerson = 'in_person',
  OnlineOnly = 'online_only',
  Hybrid = 'hybrid',
  ToBeAnnounced = 'to_be_announced',
}

export interface AuctionsEntity extends SlimAuctionsEntity {
  active_invoices: number
  allow_admins_to_refund_raffle: boolean
  allow_email_registration: boolean
  allow_phone_registration: boolean
  application_version: string
  archive_delete_date: string
  automatic_archive_enabled: boolean
  bidding_item_enabled: boolean
  pre_bidding_enabled: boolean
  base_total_raised: number
  capture_emails: CaptureAuctionFieldEnum
  capture_phone_numbers: CaptureAuctionFieldEnum
  capture_teams: CaptureAuctionFieldEnum
  capture_terms_and_conditions_agreement: boolean
  closed_banner_message: string
  closed_popup_message: string
  closing_duration: number
  closed_time: string | null
  commission_enabled: boolean
  commission_percentage: string
  consignment_shop_visible: boolean
  country: string
  created_item_types: CreatedItemTypeEnum[]
  credit_card_preauth_at_reg: boolean
  credit_card_preauth_reg_skip_allowed: boolean
  credit_card_preauthorisation: CaptureAuctionFieldEnum.Off | CaptureAuctionFieldEnum.Required
  crowdfunding_layout: boolean
  currency: string
  custom_css: string
  default_tipping_slider_amount_option: string
  description: string
  disabled: boolean
  distributor: string | null
  donation_form_message: string
  donation_item_enabled: boolean
  donate_item_enabled: boolean
  donation_target: number
  email_notifications_enabled: boolean
  enable_activity_feed: boolean
  enable_item_activity_feed: boolean
  enable_app_checkout_popup: boolean
  enable_bid_activity_poppers: boolean
  enable_bulk_invite: boolean
  enable_donation_activity_poppers: boolean
  enable_end_auction_notification: boolean
  enable_facebook_login: boolean
  enable_google_login: boolean
  enable_paddle_numbers: boolean
  enable_purchase_activity_poppers: boolean
  enable_raffle_activity_poppers: boolean
  enable_paddle_raise_pledge_activity_poppers: boolean
  enable_scheduled_time_extend: boolean
  scheduled_time_extend_end: string | null
  enable_setup_wizard: boolean
  enable_ai_assistant: boolean
  enable_ticketing: boolean
  enable_volunteers_feature: boolean
  end: string | null
  end_of_auction_promo_item: string | null
  event_capacity: number | null
  event_dates: string[]
  event_expected_guests_count: number | null
  event_location: EventLocationEntity
  event_location_type: EventLocationTypeEnum
  event_name: string
  event_ticket_additional_information: string
  event_ticket_capture_email: CaptureAuctionFieldEnum
  event_ticket_capture_phone_number: CaptureAuctionFieldEnum
  event_ticket_preview_enabled: boolean
  event_tickets_remaining: number
  fee_parity_required: boolean
  gift_aid_enabled: boolean
  global_fee_enabled: boolean
  global_fee_label: string
  global_fee_optional: PaymentMethodFeeTypeEnum
  global_flat_fee: string
  global_percentage_fee: string
  has_ioc_list: boolean
  has_vouchers: boolean
  has_only_voucher_pdfs: boolean
  homepage: string
  homepage_hub: string | null
  homepage_widget_page: string | null
  homepage_infobooth: string | null
  include_ioc_total_raised: boolean
  include_ticket_sales_in_total_raised: boolean
  invoice_bank_details: string | null
  invoice_city: string | null
  invoice_company_name: string
  invoice_country: string
  invoice_email: string
  invoice_footer_message: string
  invoice_use_auction_logo: boolean
  invoice_image: ImagesEntity | null
  invoice_more_information: string
  invoice_phone: string
  invoice_postcode: string
  invoice_state: string
  invoice_street_address: string
  invoice_suburb: string
  invoice_website: string
  is_ioc_catalog: boolean
  is_saas: boolean
  is_using_default_logo: boolean
  item_count: number
  limit_banner_height: boolean
  memorabilia_shop_visible: boolean
  metadata: Record<string, string>
  owner: string
  pending_donate_item_count: number
  paddle_numbers_require_payment_method: boolean
  paddle_start_number: number
  paused_banner_message: string
  payment_donation_component: PaymentDonationComponentEnum | null
  payment_gateway: PaymentGatewayEnum
  payment_methods: PaymentMethodsEntity[]
  purchase_item_enabled: boolean
  raffle_item_enabled: boolean
  raffles_closed: number
  raffles_drawing: number
  raffles_open: number
  raffles_paused: number
  redirect_to_auction: string | null
  redirect_until: string | null
  reg_instructions_color: string
  reg_instructions_heading: string
  reg_instructions_logo: ImagesEntity | null
  reg_instructions_show_bg: boolean
  reg_instructions_steps: Array<{ heading: string; text: string }>
  saas_trial_limit: number | string
  scheduled: boolean
  scheduled_start: string | null
  scheduled_start_enabled: boolean
  scheduled_end: string | null
  scheduled_time_extend: number
  show_banner_overlay: boolean
  sms_enabled: boolean
  sms_notifications_enabled: boolean
  sms_service: string | null
  sms_service_display_phone_number?: string
  auto_select_sms_service: boolean
  start: string | null
  stripe_connect: StripeConnectEntity | null
  stripe_secret_key: string
  stripe_user_id: string
  success_color: string
  target: number
  teams_count: number
  terms_and_conditions: string
  ticket_package_count: number
  ticket_purchases_count: number
  ticketing_guests_count: number
  tickets_label: string
  timezone: string
  tip_revenue_share_percentage: string
  tipping_callout_enabled: boolean
  tipping_callout_text: string
  tipping_fee_enabled: boolean
  tipping_slider_amount_options: string[]
  tip_brackets: TipBracketEntity[]
  trial_locked: boolean
  unlimited_event_capacity: boolean
  unpaid_invoices: number
  warn_color: string
}

export interface EventLocationEntity {
  address?: string
  name?: string
  latitude?: number
  longitude?: number
}

export interface AuctionStatusChange {
  uuid: string
  auction: string
  new_status: AuctionStatusEnum
  scheduled_start: string | null
  scheduled_end: string | null
  user_full_name: string | null
  created: string
}

export const PLACEHOLDER_EVENT_LOCATION = 'TBC'

export enum PaymentDonationComponentEnum {
  Buttons = 'buttons',
  Slider = 'slider',
}

export enum CreatedItemTypeEnum {
  Donation = 'donation',
  Raffle = 'raffle',
  Purchase = 'purchase',
  SilentAuction = 'silent_auction',
  LiveAuction = 'live_auction',
  SealedBid = 'sealed_bid',
  EventTicket = 'event_ticket',
}

export interface TipBracketEntity {
  uuid: string
  value_min: string
  value_max: string | null
  tip_percentage: string
  slider_values?: string[]
}

export interface AuctionBrandingMessages {
  closed_popup_message: string
  closed_banner_message: string
  paused_banner_message: string
}
